import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

// Define styles that will be applied inline
const buttonStyles = {
    default: {
        backgroundColor: 'var(--primary)',
        color: 'var(--primary-foreground)',
        padding: '8px 16px',
        borderRadius: '6px',
        fontWeight: '500',
        fontSize: '14px',
        border: 'none',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease' // eslint-disable-line string-to-lingui/missing-lingui-transformation
    },
    disabled: {
        opacity: '0.5',
        cursor: 'not-allowed'
    },
    destructive: {
        backgroundColor: 'var(--destructive)',
        color: 'white'
    },
    outline: {
        backgroundColor: 'var(--background)',
        color: 'var(--foreground)',
        border: '1px solid var(--border)'
    },
    secondary: {
        backgroundColor: 'var(--secondary)',
        color: 'var(--secondary-foreground)'
    },
    ghost: {
        backgroundColor: 'transparent',
        color: 'var(--foreground)'
    },
    small: {
        padding: '4px 12px',
        fontSize: '12px'
    },
    large: {
        padding: '12px 20px',
        fontSize: '16px'
    }
}

// Keep the original implementation for compatibility
const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                destructive:
                    'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                outline: 'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                ghost: 'hover:bg-accent hover:text-accent-foreground', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                link: 'text-primary underline-offset-4 hover:underline', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                brand: 'bg-[#2F6868] hover:bg-[#2F6868]/90 border-[#2F6868] text-white' // eslint-disable-line string-to-lingui/missing-lingui-transformation
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
)

type ButtonProps = React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean
    }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = 'default', size = 'default', asChild = false, disabled = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button'

        // Create the style object based on variant and size
        const style: React.CSSProperties = {
            ...buttonStyles.default,
            ...(variant === 'destructive' ? buttonStyles.destructive : {}),
            ...(variant === 'outline' ? buttonStyles.outline : {}),
            ...(variant === 'secondary' ? buttonStyles.secondary : {}),
            ...(variant === 'ghost' ? buttonStyles.ghost : {}),
            ...(size === 'sm' ? buttonStyles.small : {}),
            ...(size === 'lg' ? buttonStyles.large : {}),
            ...(disabled ? buttonStyles.disabled : {})
        }

        // We'll still use the className for users who want to add custom classes
        return <Comp data-slot='button' className={className} style={style} disabled={disabled} ref={ref} {...props} />
    }
)

Button.displayName = 'Button' // eslint-disable-line string-to-lingui/missing-lingui-transformation

export { Button, buttonVariants, type ButtonProps }
